import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  SlidersHorizontal,
  Search as SearchIcon,
  X,
  ChevronDown,
  Compass,
  Layers,
  Landmark,
  Coins,
  Clock,
  ShieldAlert,
  Tag,
} from "lucide-react";
import { Header } from "@/components/homepage/Header";
import { Footer } from "@/components/homepage/Footer";
import { OpportunityCard } from "@/components/homepage/OpportunityCard";
import { OpportunityCardSkeleton } from "@/components/OpportunityCardSkeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useOpportunities } from "@/hooks/useOpportunities";
import { SORT_OPTIONS, sortOpportunities } from "@/utils/opportunitySort";
import { LABEL_DEFINITIONS, deriveLabels } from "@/utils/opportunityLabels";
import { deriveCapitalUSD, deriveDaysAgo } from "@/utils/opportunityDerived";
import { matchesQuery } from "@/utils/searchIntelligence";
import { STATUS_MAP } from "@/utils/intelligence";
import { LIST_PAGE } from "@/constants/testIds";

/* ─── Filter option catalogues ────────────────────────────────── */

const CAPITAL_BUCKETS = [
  { value: "low",  label: "أقل من 10 مليون دولار",    min: 0, max: 10_000_000 },
  { value: "mid",  label: "10–50 مليون دولار",         min: 10_000_000, max: 50_000_000 },
  { value: "high", label: "أكثر من 50 مليون دولار",    min: 50_000_000, max: Infinity },
];

const SCORE_BUCKETS = [
  { value: "high",   label: "عالية (80+)",       min: 80, max: 100 },
  { value: "medium", label: "متوسطة (65–79)",   min: 65, max: 79  },
  { value: "low",    label: "منخفضة (< 65)",     min: 0,  max: 64  },
];

const TIME_OPTIONS = [
  { value: "urgent",   label: "عاجل" },
  { value: "30-days",  label: "خلال 30 يوماً" },
  { value: "flexible", label: "مرن الأمد" },
];

const RECENCY_OPTIONS = [
  { value: "today",  label: "اليوم" },
  { value: "24h",    label: "آخر 24 ساعة" },
  { value: "week",   label: "هذا الأسبوع" },
  { value: "month",  label: "هذا الشهر" },
  { value: "urgent", label: "عاجلة الآن" },
];

const RISK_OPTIONS = [
  { value: "none",   label: "بدون مخاطر مسجّلة" },
  { value: "low",    label: "مخاطر منخفضة" },
  { value: "medium", label: "مخاطر متوسطة" },
  { value: "high",   label: "مخاطر مرتفعة" },
];

const STATUS_OPTIONS = Object.values(STATUS_MAP).map((s) => ({ value: s.key, label: s.label }));
const LABEL_OPTIONS  = LABEL_DEFINITIONS.map((l) => ({ value: l.key, label: l.label }));

/* ─── Match / filter engine (unchanged) ───────────────────────── */

const opportunityMatches = (o, meta, f) => {
  if (f.search && !matchesQuery(o, f.search)) return false;
  if (f.type && o.type !== f.type) return false;
  if (f.country && o.country !== f.country) return false;
  if (f.industry && o.industry !== f.industry) return false;

  if (f.recency) {
    const days = deriveDaysAgo(o.publishedAt);
    if (f.recency === "today"  && days > 0)                            return false;
    if (f.recency === "24h"    && days > 1)                            return false;
    if (f.recency === "week"   && days > 7)                            return false;
    if (f.recency === "month"  && days > 30)                           return false;
    if (f.recency === "urgent" && meta.timeSensitivity !== "urgent")   return false;
  }
  if (f.score) {
    const b = SCORE_BUCKETS.find((x) => x.value === f.score);
    if (b && (o.score < b.min || o.score > b.max)) return false;
  }
  if (f.capital) {
    const b = CAPITAL_BUCKETS.find((x) => x.value === f.capital);
    const cap = deriveCapitalUSD(meta);
    if (!b || cap === null || cap < b.min || cap > b.max) return false;
  }
  if (f.time && meta.timeSensitivity !== f.time) return false;
  if (f.risk) {
    const risks = meta.risks || [];
    if (f.risk === "none" && risks.length !== 0) return false;
    if (f.risk !== "none" && !risks.some((r) => r.level === f.risk)) return false;
  }
  if (f.status && meta.status !== f.status) return false;
  if (f.label) {
    const labels = deriveLabels({ ...o, meta }).map((l) => l.key);
    if (!labels.includes(f.label)) return false;
  }
  return true;
};

/* ─── Chip-style select — cleaner than a native dropdown ────── */

const Select = ({ label, value, onChange, options, allLabel, testId }) => (
  <label className="flex min-w-0 flex-col gap-1.5">
    <span className="text-[11px] font-medium uppercase tracking-widest text-ink-500">
      {label}
    </span>
    <div className="relative">
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full appearance-none rounded-lg border border-surface-200 bg-white ps-3 pe-9 text-sm font-medium text-ink-900 shadow-sm outline-none transition-all hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
      >
        {allLabel !== undefined && <option value="">{allLabel}</option>}
        {options.map((opt) => (
          <option key={opt.value ?? opt} value={opt.value ?? opt}>
            {opt.label ?? opt}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute inset-y-0 end-3 my-auto h-3.5 w-3.5 text-ink-500"
        strokeWidth={1.75}
        aria-hidden
      />
    </div>
  </label>
);

/* ─── Grouped filter panel ────────────────────────────────────── */

const FilterGroup = ({ icon: Icon, title, children, testId }) => (
  <div data-testid={testId} className="border-b border-surface-100 pb-5 last:border-b-0 last:pb-0">
    <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-fursa-700">
      <Icon className="h-3 w-3" strokeWidth={2} />
      {title}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const FilterPanel = ({
  search, setSearch,
  type, setType, country, setCountry, industry, setIndustry,
  capital, setCapital, score, setScore,
  time, setTime, recency, setRecency, risk, setRisk, status, setStatus, label, setLabel,
  uniqueTypes, uniqueCountries, uniqueIndustries,
}) => (
  <div className="space-y-5">
    {/* Search input — always at the top of the panel */}
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-widest text-ink-500">
        البحث
      </span>
      <div className="relative">
        <SearchIcon
          className="pointer-events-none absolute inset-y-0 end-3 my-auto h-4 w-4 text-ink-500"
          strokeWidth={1.75}
          aria-hidden
        />
        <input
          type="search"
          data-testid={LIST_PAGE.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ابحث في العنوان أو الملخّص…"
          className="h-11 w-full rounded-lg border border-surface-200 bg-white ps-3 pe-10 text-sm text-ink-900 shadow-sm outline-none transition-all placeholder:text-ink-500/60 hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
        />
      </div>
    </label>

    <FilterGroup icon={Layers} title="ما نوع الفرصة؟" testId={LIST_PAGE.filterGroup("what")}>
      <Select label="النوع"    allLabel="كل الأنواع"    value={type}     onChange={setType}     options={uniqueTypes}    testId={LIST_PAGE.filterType} />
      <Select label="القطاع"   allLabel="كل القطاعات"   value={industry} onChange={setIndustry} options={uniqueIndustries} testId={LIST_PAGE.filterIndustry} />
    </FilterGroup>

    <FilterGroup icon={Compass} title="أين؟" testId={LIST_PAGE.filterGroup("where")}>
      <Select label="الدولة" allLabel="كل الدول" value={country} onChange={setCountry} options={uniqueCountries} testId={LIST_PAGE.filterCountry} />
    </FilterGroup>

    <FilterGroup icon={Landmark} title="حجم الاستثمار وجودته" testId={LIST_PAGE.filterGroup("investment")}>
      <Select label="رأس المال" allLabel="أي حجم"   value={capital} onChange={setCapital} options={CAPITAL_BUCKETS} testId={LIST_PAGE.filterCapital} />
      <Select label="النقاط"   allLabel="كل النقاط" value={score}   onChange={setScore}   options={SCORE_BUCKETS}   testId={LIST_PAGE.filterScore} />
    </FilterGroup>

    <FilterGroup icon={Clock} title="التوقيت والحداثة" testId={LIST_PAGE.filterGroup("time")}>
      <Select label="الإلحاح الزمني" allLabel="أي وقت"      value={time}    onChange={setTime}    options={TIME_OPTIONS}    testId={LIST_PAGE.filterTime} />
      <Select label="الحداثة"        allLabel="أي فترة"     value={recency} onChange={setRecency} options={RECENCY_OPTIONS} testId={LIST_PAGE.filterRecency} />
    </FilterGroup>

    <FilterGroup icon={ShieldAlert} title="المخاطر والحالة" testId={LIST_PAGE.filterGroup("risk")}>
      <Select label="مستوى المخاطرة" allLabel="أي مستوى"      value={risk}   onChange={setRisk}   options={RISK_OPTIONS}   testId={LIST_PAGE.filterRisk} />
      <Select label="الحالة"          allLabel="كل الحالات"    value={status} onChange={setStatus} options={STATUS_OPTIONS} testId={LIST_PAGE.filterStatus} />
      <Select label="وسم موصى به"   allLabel="كل الوسوم"     value={label}  onChange={setLabel}  options={LABEL_OPTIONS}  testId={LIST_PAGE.filterLabel} />
    </FilterGroup>
  </div>
);

/* ─── Active-filter chip strip ───────────────────────────────── */

const CHIP_LABELS = {
  type:     "النوع",
  country:  "الدولة",
  industry: "القطاع",
  recency:  "الحداثة",
  capital:  "رأس المال",
  score:    "النقاط",
  time:     "الإلحاح",
  risk:     "المخاطرة",
  status:   "الحالة",
  label:    "وسم",
};

const chipValueLabel = (key, value) => {
  switch (key) {
    case "capital": return CAPITAL_BUCKETS.find((b) => b.value === value)?.label ?? value;
    case "score":   return SCORE_BUCKETS.find((b) => b.value === value)?.label ?? value;
    case "time":    return TIME_OPTIONS.find((b) => b.value === value)?.label ?? value;
    case "recency": return RECENCY_OPTIONS.find((b) => b.value === value)?.label ?? value;
    case "risk":    return RISK_OPTIONS.find((b) => b.value === value)?.label ?? value;
    case "status":  return STATUS_OPTIONS.find((b) => b.value === value)?.label ?? value;
    case "label":   return LABEL_OPTIONS.find((b) => b.value === value)?.label ?? value;
    default:        return value;
  }
};

const ActiveChips = ({ filters, setters, onClearAll }) => {
  const entries = Object.entries(filters).filter(
    ([k, v]) => v && v !== "" && k !== "search",
  );
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={LIST_PAGE.activeChips}>
      {entries.map(([k, v]) => (
        <button
          key={k}
          type="button"
          onClick={() => setters[k]("")}
          data-testid={LIST_PAGE.activeChip(k)}
          className="inline-flex items-center gap-1.5 rounded-full border border-fursa-200 bg-fursa-50 py-1 pe-2 ps-3 text-xs font-medium text-fursa-700 transition-colors hover:bg-fursa-100"
        >
          <span className="text-fursa-500/80">{CHIP_LABELS[k]}</span>
          <span>·</span>
          <span className="font-semibold">{chipValueLabel(k, v)}</span>
          <span className="ms-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-fursa-100 text-fursa-700 group-hover:bg-fursa-200">
            <X className="h-2.5 w-2.5" strokeWidth={2.5} />
          </span>
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        data-testid={LIST_PAGE.clearAllChips}
        className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-ink-500 underline-offset-4 hover:text-ink-900 hover:underline"
      >
        مسح الكل
      </button>
    </div>
  );
};

/* ─── Error state ─────────────────────────────────────────────── */

const ErrorState = ({ onRetry }) => (
  <div
    data-testid={LIST_PAGE.error}
    className="mt-6 flex flex-col items-center justify-center overflow-hidden rounded-2xl border border-risk-100 bg-risk-50/40 px-6 py-16 text-center"
    role="alert"
    aria-live="assertive"
  >
    <svg className="mb-5 h-12 w-12 text-risk-700" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 9v3.75m0 4.5v.008M4.5 19.5h15a1.5 1.5 0 0 0 1.3-2.25L13.3 4.5a1.5 1.5 0 0 0-2.6 0L3.2 17.25a1.5 1.5 0 0 0 1.3 2.25Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
    <h3 className="text-lg font-semibold tracking-tight text-ink-900">
      تعذّر تحميل الفرص
    </h3>
    <p className="mt-2 max-w-md text-sm leading-relaxed text-ink-500">
      حدث خطأ أثناء الاتصال بالخادم. تحقّق من الاتصال ثم أعد المحاولة.
    </p>
    <button
      type="button"
      onClick={onRetry}
      data-testid={LIST_PAGE.errorRetry}
      className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-fursa-800"
    >
      إعادة المحاولة
    </button>
  </div>
);

/* ─── Empty state ─────────────────────────────────────────────── */

const PremiumEmpty = ({ onClearAll, hasActive }) => (
  <div
    data-testid={LIST_PAGE.empty}
    className="mt-6 flex flex-col items-center justify-center overflow-hidden rounded-2xl border border-dashed border-surface-200 bg-white px-6 py-20 text-center"
  >
    {/* Line-art illustration — Fursa geometric mark exploded */}
    <svg className="mb-6 h-24 w-24 text-fursa-600" viewBox="0 0 96 96" fill="none" aria-hidden>
      <circle cx="48" cy="48" r="30" stroke="currentColor" strokeWidth="1.2" opacity="0.25" />
      <circle cx="48" cy="48" r="20" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
      <circle cx="48" cy="48" r="10" stroke="currentColor" strokeWidth="1.6" />
      <line x1="48" y1="6"  x2="48" y2="18" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
      <line x1="48" y1="78" x2="48" y2="90" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
      <line x1="6"  y1="48" x2="18" y2="48" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
      <line x1="78" y1="48" x2="90" y2="48" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
      <circle cx="48" cy="48" r="2" fill="currentColor" />
    </svg>

    <h3 className="text-xl font-semibold tracking-tight text-ink-900">
      لا توجد فرص مطابقة{hasActive ? " للفلاتر الحالية" : ""}
    </h3>
    <p className="mt-3 max-w-md text-sm leading-relaxed text-ink-500">
      {hasActive
        ? "جرّب توسيع نطاق البحث أو إزالة بعض الفلاتر. الفرص التي لا تظهر الآن قد تكون خارج نطاق ما اخترته."
        : "لم يُنشَر بعد ما يطابق ما تبحث عنه. تابع لاحقاً — نُحدِّث الفرص أسبوعياً."}
    </p>
    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
      {hasActive && (
        <button
          type="button"
          onClick={onClearAll}
          data-testid={LIST_PAGE.emptyReset}
          className="inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-fursa-800"
        >
          إعادة تعيين الفلاتر
        </button>
      )}
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-5 py-2.5 text-sm font-medium text-ink-900 transition-colors hover:bg-surface-50"
      >
        العودة إلى الرئيسية
      </Link>
    </div>
  </div>
);

/* ─── Page ────────────────────────────────────────────────────── */

export default function OpportunitiesListPage() {
  const {
    opportunities,
    countries: countryList,
    isLoading: isLoadingOpps,
    error: bootstrapError,
    refresh,
  } = useOpportunities();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search,   setSearch]   = useState(searchParams.get("q")        ?? "");
  const [type,     setType]     = useState(searchParams.get("type")     ?? "");
  const [country,  setCountry]  = useState(searchParams.get("country")  ?? "");
  const [industry, setIndustry] = useState(searchParams.get("industry") ?? "");
  const [recency,  setRecency]  = useState(searchParams.get("recency")  ?? "");
  const [capital,  setCapital]  = useState(searchParams.get("capital")  ?? "");
  const [score,    setScore]    = useState(searchParams.get("score")    ?? "");
  const [time,     setTime]     = useState(searchParams.get("time")     ?? "");
  const [risk,     setRisk]     = useState(searchParams.get("risk")     ?? "");
  const [status,   setStatus]   = useState(searchParams.get("status")   ?? "");
  const [label,    setLabel]    = useState(searchParams.get("label")    ?? "");
  const [sort,     setSort]     = useState(searchParams.get("sort")     ?? "recommended");
  const [sheetOpen, setSheetOpen] = useState(false);

  // Client-side "load more" pagination — reveals PAGE_SIZE cards at a
  // time. Keeps first-render lightweight and scales as more content
  // ships without requiring backend pagination today.
  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const isLoading = isLoadingOpps;

  const uniqueTypes      = useMemo(() => Array.from(new Set(opportunities.map((o) => o.type))),      [opportunities]);
  const uniqueCountries  = useMemo(() => countryList.map((c) => c.name),                             [countryList]);
  const uniqueIndustries = useMemo(() => Array.from(new Set(opportunities.map((o) => o.industry))),  [opportunities]);

  // Consolidated URL sync — every filter, sort, and search term is
  // reflected in the URL so state is bookmarkable, shareable, and
  // back-button-safe.
  useEffect(() => {
    const next = new URLSearchParams();
    const write = (k, v) => { if (v) next.set(k, v); };
    write("q",        search);
    write("type",     type);
    write("country",  country);
    write("industry", industry);
    write("recency",  recency);
    write("capital",  capital);
    write("score",    score);
    write("time",     time);
    write("risk",     risk);
    write("status",   status);
    write("label",    label);
    if (sort && sort !== "recommended") next.set("sort", sort);
    setSearchParams(next, { replace: true });
  }, [search, type, country, industry, recency, capital, score, time, risk, status, label, sort, setSearchParams]);

  const filters = { search, type, country, industry, recency, capital, score, time, risk, status, label };
  const setters = { search: setSearch, type: setType, country: setCountry, industry: setIndustry, recency: setRecency, capital: setCapital, score: setScore, time: setTime, risk: setRisk, status: setStatus, label: setLabel };

  const filterProps = {
    search, setSearch, type, setType, country, setCountry, industry, setIndustry,
    recency, setRecency, capital, setCapital, score, setScore, time, setTime, risk, setRisk,
    status, setStatus, label, setLabel,
    uniqueTypes, uniqueCountries, uniqueIndustries,
  };

  const filtered = useMemo(() => {
    const list = opportunities
      .map((o) => ({ ...o, meta: o.meta ?? {} }))
      .filter((o) => opportunityMatches(o, o.meta, filters));
    return sortOpportunities(list, sort);
  }, [opportunities, search, type, country, industry, recency, capital, score, time, risk, status, label, sort]);

  // Reset the visible window whenever the filtered/sorted result set
  // changes — otherwise a user narrowing filters could scroll past the
  // (now smaller) tail with a stale window.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, type, country, industry, recency, capital, score, time, risk, status, label, sort]);

  const visible    = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const canLoadMore = filtered.length > visible.length;

  const activeCount = Object.entries(filters).filter(([, v]) => v && v !== "").length;
  const hasActive = activeCount > 0;

  const clearAll = () => {
    setSearch(""); setType(""); setCountry(""); setIndustry("");
    setRecency(""); setCapital(""); setScore(""); setTime(""); setRisk("");
    setStatus(""); setLabel(""); setSort("recommended");
  };

  return (
    <div className="min-h-screen bg-surface-50 text-ink-900">
      <Header />
      <main data-testid={LIST_PAGE.root}>
        {/* Hero band */}
        <section className="relative overflow-hidden bg-surface-50">
          <div className="pointer-events-none absolute inset-0 fursa-grid-bg opacity-70" aria-hidden />
          <div className="relative mx-auto max-w-7xl px-4 pt-12 pb-8 sm:px-6 sm:pt-14 lg:px-8 lg:pt-16">
            <p className="fursa-eyebrow">Opportunities</p>
            <h1
              data-testid={LIST_PAGE.title}
              className="mt-3 text-3xl font-bold tracking-tightest text-ink-900 sm:text-4xl lg:text-5xl"
            >
              فرص الأعمال المُختارة يدويًا
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink-500 sm:text-lg">
              كل فرصة تمرّ عبر مراجعة تحريرية قبل النشر — لتضمن أنّ ما تراه هنا يستحقّ وقتك في اتخاذ القرار.
            </p>
          </div>
        </section>

        {/* Body — sidebar (desktop) + results */}
        <section className="mx-auto max-w-7xl px-4 pb-16 pt-8 sm:px-6 lg:px-8 lg:pb-24 lg:pt-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr] xl:grid-cols-[300px_1fr]">
            {/* ── Sticky filter sidebar (desktop) ── */}
            <aside className="hidden lg:block">
              <div
                data-testid={LIST_PAGE.filterPanel}
                className="sticky top-24 rounded-2xl border border-surface-200 bg-white p-5 shadow-card"
              >
                <div className="mb-5 flex items-center justify-between border-b border-surface-100 pb-4">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4 text-fursa-700" strokeWidth={1.75} />
                    <h2 className="text-sm font-semibold text-ink-900">الفلاتر</h2>
                    {activeCount > 0 && (
                      <span
                        dir="ltr"
                        className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-fursa-700 px-1.5 font-mono text-[10px] font-bold text-white"
                      >
                        {activeCount}
                      </span>
                    )}
                  </div>
                  {hasActive && (
                    <button
                      type="button"
                      onClick={clearAll}
                      data-testid={LIST_PAGE.clearFilters}
                      className="text-xs font-medium text-ink-500 underline-offset-4 hover:text-fursa-700 hover:underline"
                    >
                      مسح الكل
                    </button>
                  )}
                </div>
                <FilterPanel {...filterProps} />
              </div>
            </aside>

            {/* ── Results column ── */}
            <div className="min-w-0">
              {/* Toolbar (result count · sort · mobile-filter trigger) */}
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-surface-200 bg-white px-4 py-3 shadow-card">
                <div className="flex items-center gap-3">
                  <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger asChild>
                      <button
                        type="button"
                        data-testid={LIST_PAGE.mobileFiltersTrigger}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 shadow-sm transition-colors hover:border-fursa-300 lg:hidden"
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
                        الفلاتر
                        {activeCount > 0 && (
                          <span
                            dir="ltr"
                            className="ms-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-fursa-700 px-1 font-mono text-[10px] font-bold text-white"
                          >
                            {activeCount}
                          </span>
                        )}
                      </button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
                      <SheetHeader>
                        <SheetTitle className="text-start">الفلاتر</SheetTitle>
                      </SheetHeader>
                      <div className="mt-4">
                        <FilterPanel {...filterProps} />
                      </div>
                      <div className="mt-6 flex items-center justify-between border-t border-surface-100 pt-4">
                        {hasActive ? (
                          <button
                            type="button"
                            onClick={clearAll}
                            data-testid={LIST_PAGE.clearFilters + "-mobile"}
                            className="text-sm font-medium text-ink-700 underline-offset-4 hover:text-fursa-700 hover:underline"
                          >
                            مسح جميع الفلاتر
                          </button>
                        ) : (
                          <span />
                        )}
                        <button
                          type="button"
                          onClick={() => setSheetOpen(false)}
                          className="inline-flex items-center rounded-lg bg-fursa-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-fursa-800"
                        >
                          عرض النتائج
                        </button>
                      </div>
                    </SheetContent>
                  </Sheet>

                  <p data-testid={LIST_PAGE.resultsCount} className="text-sm text-ink-500">
                    <span dir="ltr" className="font-mono font-bold tabular-nums text-fursa-700">
                      {filtered.length}
                    </span>{" "}
                    فرصة {hasActive ? "مطابقة" : "متاحة"}
                  </p>
                </div>

                <label className="flex items-center gap-2">
                  <span className="hidden text-[11px] font-medium uppercase tracking-widest text-ink-500 sm:inline">
                    رتّب حسب
                  </span>
                  <div className="relative">
                    <select
                      data-testid={LIST_PAGE.filterSort}
                      value={sort}
                      onChange={(e) => setSort(e.target.value)}
                      className="h-9 appearance-none rounded-lg border border-surface-200 bg-white ps-3 pe-8 text-sm font-medium text-ink-900 shadow-sm outline-none transition-all hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
                    >
                      {SORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <ChevronDown
                      className="pointer-events-none absolute inset-y-0 end-2 my-auto h-3.5 w-3.5 text-ink-500"
                      strokeWidth={1.75}
                    />
                  </div>
                </label>
              </div>

              {/* Active filter chips */}
              {hasActive && (
                <div className="mb-4">
                  <ActiveChips filters={filters} setters={setters} onClearAll={clearAll} />
                </div>
              )}

              {/* Results grid */}
              {isLoading ? (
                <div
                  data-testid={LIST_PAGE.loadingSkeleton}
                  className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
                  aria-busy="true"
                  aria-live="polite"
                >
                  {Array.from({ length: 6 }).map((_, i) => <OpportunityCardSkeleton key={i} />)}
                </div>
              ) : bootstrapError ? (
                <ErrorState onRetry={refresh} />
              ) : filtered.length === 0 ? (
                <PremiumEmpty onClearAll={clearAll} hasActive={hasActive} />
              ) : (
                <>
                  <div data-testid={LIST_PAGE.grid} className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {visible.map((o) => <OpportunityCard key={o.id} opportunity={o} />)}
                  </div>

                  {/* Load-more — pure client-side chunking today. When
                      backend pagination lands, swap the trigger for a
                      server call without changing the UX. */}
                  {canLoadMore && (
                    <div className="mt-8 flex flex-col items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                        data-testid={LIST_PAGE.loadMore}
                        className="inline-flex items-center gap-2 rounded-lg border border-surface-200 bg-white px-5 py-2.5 text-sm font-semibold text-ink-900 shadow-sm transition-all hover:-translate-y-0.5 hover:border-fursa-300 hover:text-fursa-700"
                      >
                        عرض المزيد
                        <span dir="ltr" className="inline-flex items-center gap-1 font-mono text-[11px] tabular-nums text-ink-500">
                          {visible.length}/{filtered.length}
                        </span>
                      </button>
                      <p className="text-[11px] text-ink-500">
                        تعرض حالياً <span dir="ltr" className="font-mono tabular-nums">{visible.length}</span> من أصل <span dir="ltr" className="font-mono tabular-nums">{filtered.length}</span>
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
